import * as Mantine from '@mantine/core';
import { BarChart, LineChart } from '@mantine/charts';
import { DatePicker, DateTimePicker, DatePickerInput, TimePicker } from '@mantine/dates';

import { TIME_PICKER_FORMAT } from 'constants/dateTime';

import { ArrowLeftIconMd, ArrowRightIconMd, ArrowRightIconSm, CheckIconSm, CheckIconXs, ChevronDownIconSm, ChevronLeftIconSm, ChevronRightIconSm, CloseIconMd, CloseIconSm, CloseIconXs, EyeIconSm, EyeOffIconSm, MoreIconSm } from '@chownow/pantry/icons';

import { CustomCheckboxIcon } from './theme.helpers';

import styles from './theme.module.scss';

export const componentsConfig = {
  Accordion: Mantine.Accordion.extend({
    defaultProps: {
      chevron: <ChevronDownIconSm />,
    },
    classNames: {
      root: styles.accordion,
      chevron: styles.accordionChevron,
      control: styles.accordionControl,
      item: styles.accordionItem,
      content: styles.accordionContent,
      label: styles.accordionLabel,
    },
  }),
  ActionIcon: Mantine.ActionIcon.extend({
    defaultProps: {
      radius: 'xl2',
      variant: 'link',
    },
    classNames: {
      root: styles.actionIcon,
      icon: styles.actionIconIcon,
    },
  }),
  ActionIconGroup: Mantine.ActionIconGroup.extend({
    classNames: {
      group: styles.actionIconGroup,
    },
  }),
  // https://mantine.dev/core/alert/
  Alert: Mantine.Alert.extend({
    defaultProps: {
      size: 'sm',
    },
    classNames: {
      root: styles.alert,
      wrapper: styles.alertWrapper,
      message: styles.alertMessage,
      title: styles.alertTitle,
      icon: styles.alertIcon,
    },
  }),
  // https://mantine.dev/core/anchor/
  Anchor: Mantine.Anchor.extend({
    defaultProps: {
      size: 'sm',
      underline: 'always',
    },
    classNames: {
      root: styles.anchor,
    },
  }),
  AppShell: Mantine.AppShell.extend({
    classNames: {
      aside: styles.appShellAside,
      navbar: styles.appShellNavbar,
    },
  }),
  Avatar: Mantine.Avatar.extend({
    defaultProps: {
      color: 'var(--color-neutral-8)',
      variant: 'filled',
    },
    classNames: {
      root: styles.avatar,
      placeholder: styles.avatarPlaceholder,
    },
  }),
  // https://mantine.dev/core/badge/
  Badge: Mantine.Badge.extend({
    defaultProps: {
      radius: 4,
    },
    classNames: {
      root: styles.badge,
    },
  }),
  BarChart: BarChart.extend({
    defaultProps: {
      strokeDasharray: '2 2',
      tickLine: 'none',
      gridProps: { stroke: 'var(--border-secondary)' },
    },
    classNames: {
      axis: styles.barChartAxis,
    },
  }),
  // https://mantine.dev/core/button/
  Button: Mantine.Button.extend({
    defaultProps: {
      size: 'md',
      variant: 'filled',
    },
    classNames: {
      root: styles.button,
      inner: styles.buttonInner,
      section: styles.buttonSection,
      label: styles.buttonLabel,
    },
  }),
  ButtonGroup: Mantine.ButtonGroup.extend({
    classNames: {
      group: styles.buttonGroup,
    },
  }),
  // https://mantine.dev/core/card/
  Card: Mantine.Card.extend({
    defaultProps: {
      radius: 'var(--mantine-radius-sm)',
      size: 'md',
    },
    classNames: {
      root: styles.card,
    },
  }),
  Checkbox: Mantine.Checkbox.extend({
    defaultProps: {
      icon: CustomCheckboxIcon,
      radius: 'var(--mantine-radius-xs2)',
    },
    classNames: {
      input: styles.checkboxInput,
      label: styles.inputControlLabel,
      labelWrapper: styles.checkboxLabelWrapper,
      icon: styles.checkboxIcon,
    },
  }),
  // https://mantine.dev/core/code/
  Code: Mantine.Code.extend({
    classNames: {
      root: styles.code,
    },
  }),
  Combobox: Mantine.Combobox.extend({
    classNames: {
      groupLabel: styles.comboboxGroupLabel,
      option: styles.comboboxOption,
    },
  }),
  DatePicker: DatePicker.extend({
    defaultProps: {
      firstDayOfWeek: 0,
      nextIcon: <ArrowRightIconMd />,
      previousIcon: <ArrowLeftIconMd />,
      withCellSpacing: false,
    },
    classNames: {
      calendarHeader: styles.calendarHeader,
      calendarHeaderLevel: styles.calendarHeaderLevel,
      calendarHeaderControl: styles.calendarHeaderControl,
      day: styles.calendarDay,
      weekday: styles.calendarWeekday,
    },
  }),
  DateTimePicker: DateTimePicker.extend({
    defaultProps: {
      firstDayOfWeek: 0,
      nextIcon: <ArrowRightIconMd />,
      previousIcon: <ArrowLeftIconMd />,
      withCellSpacing: false,
      submitButtonProps: {
        variant: 'filled',
        size: 'sm',
        children: <ArrowRightIconSm style={{ color: 'white' }} />,
      },
      timePickerProps: {
        withDropdown: true,
        popoverProps: { withinPortal: false },
        format: TIME_PICKER_FORMAT,
      },
    },
    classNames: {
      // using same classes as datePicker
      calendarHeader: styles.calendarHeader,
      calendarHeaderLevel: styles.calendarHeaderLevel,
      calendarHeaderControl: styles.calendarHeaderControl,
      day: styles.calendarDay,
      weekday: styles.calendarWeekday,
      input: styles.dateTimePickerInput,
      root: styles.dateTimePicker,
      timeInput: styles.dateTimePickerTimeInput,
      timeWrapper: styles.dateTimePickerWrapper,
      month: styles.calendarMonth,
    },
  }),
  DatePickerInput: DatePickerInput.extend({
    defaultProps: {
      firstDayOfWeek: 0,
      nextIcon: <ArrowRightIconMd />,
      previousIcon: <ArrowLeftIconMd />,
      withCellSpacing: false,
    },
    classNames: {
      calendarHeader: styles.calendarHeader,
      calendarHeaderLevel: styles.calendarHeaderLevel,
      calendarHeaderControl: styles.calendarHeaderControl,
      day: styles.calendarDay,
      input: styles.datePickerInput,
      weekday: styles.calendarWeekday,
    },
  }),
  // https://mantine.dev/core/drawer/
  Drawer: Mantine.Drawer.extend({
    defaultProps: {
      closeButtonProps: {
        icon: <CloseIconMd />,
        variant: 'transparent',
        size: 24,
      },
      overlayProps: {
        backgroundOpacity: 1,
      },
      position: 'right',
      size: 'lg',
    },
    classNames: {
      body: styles.drawerBody,
      header: styles.drawerHeader,
      root: styles.drawer,
    },
  }),
  Divider: Mantine.Divider.extend({
    defaultProps: {
      color: 'var(--border-secondary)',
    },
  }),
  // https://v3.mantine.dev/core/grid/
  Grid: Mantine.Grid.extend({
    defaultProps: {
      gutter: { lg: 'xl', md: 'md', sm: 'sm' },
      justify: 'center',
    },
  }),
  Group: Mantine.Group.extend({
    classNames: {
      root: styles.group,
    },
  }),
  // https://mantine.dev/core/indicator/
  Indicator: Mantine.Indicator.extend({
    defaultProps: {
      size: 'var(--dimension-xs)',
    },
    classNames: {
      indicator: styles.indicator,
      root: styles.indicatorRoot,
    },
  }),
  Input: Mantine.Input.extend({
    defaultProps: {
      size: 'md',
    },
    classNames: {
      input: styles.input,
      wrapper: styles.inputWrapper,
      section: styles.inputSection,
    },
  }),
  InputWrapper: Mantine.InputWrapper.extend({
    defaultProps: {
      inputWrapperOrder: ['label', 'input', 'description', 'error'],
    },
    classNames: {
      description: styles.inputDescription,
      label: styles.inputLabel,
      error: styles.inputError,
      required: styles.inputWrapperRequired,
    },
  }),
  LineChart: LineChart.extend({
    defaultProps: {
      strokeDasharray: '2 2',
      tickLine: 'none',
      curveType: 'linear',
      withDots: true,
      dotProps: { display: 'none' },
      gridProps: { stroke: 'var(--border-secondary)' },
    },
    classNames: {
      axis: styles.lineChartAxis,
    },
  }),
  // https://mantine.dev/core/list/
  List: Mantine.List.extend({
    classNames: {
      root: styles.list,
      item: styles.listItem,
    },
  }),
  Loader: Mantine.Loader.extend({
    defaultProps: {
      color: 'var(--border-primary)',
      size: 'lg',
    },
    classNames: {
      root: styles.loader,
    },
  }),
  Menu: Mantine.Menu.extend({
    defaultProps: {
      shadow: 'sm',
    },
    classNames: {
      divider: styles.menuDivider,
      dropdown: styles.menuDropdown,
      item: styles.menuItem,
      itemSection: styles.menuItemSection,
    },
    // Using this to override popover default width
    styles: {
      dropdown: { width: 'max-content' },
    },
  }),
  // https://mantine.dev/core/modal/
  Modal: Mantine.Modal.extend({
    defaultProps: {
      centered: true,
      overlayProps: {
        blur: 0,
        backgroundOpacity: 1,
      },
      closeButtonProps: {
        icon: <CloseIconMd />,
        variant: 'transparent',
        size: 24,
      },
      size: 'md',
    },
    classNames: {
      body: styles.modalBody,
      close: styles.modalClose,
      content: styles.modalContent,
      header: styles.modalHeader,
      root: styles.modal,
      title: styles.modalTitle,
    },
  }),
  MultiSelect: Mantine.MultiSelect.extend({
    defaultProps: {
      rightSection: <ChevronDownIconSm />,
      renderOption: ({ option, checked }) => (
        <>
          {option.label}
          {checked && <CheckIconSm />}
        </>
      ),
    },
    classNames: {
      dropdown: styles.selectDropdown,
      input: styles.multiSelectInput,
      pill: styles.multiSelectPill,
      pillsList: styles.multiSelectPillsList,
      option: styles.selectOption,
    },
  }),
  NavLink: Mantine.NavLink.extend({
    classNames: {
      root: styles.navLink,
      label: styles.navLinkLabel,
      children: styles.navLinkChildren,
      section: styles.navLinkSection,
    },
  }),
  // https://mantine.dev/core/notification/
  Notification: Mantine.Notification.extend({
    defaultProps: {
      size: 'compact',
      withCloseButton: false,
    },
    classNames: {
      body: styles.notificationBody,
      closeButton: styles.notificationCloseButton,
      description: styles.notificationDescription,
      root: styles.notification,
      icon: styles.notificationIcon,
    },
  }),
  // https://mantine.dev/core/number-input/
  NumberInput: Mantine.NumberInput.extend({
    defaultProps: {
      size: 'md',
      rightSectionPointerEvents: 'all',
      rightSection: <CloseIconSm aria-label="Clear input" />,
      mb: 'lg',
    },
  }),
  Overlay: Mantine.Overlay.extend({
    defaultProps: {
      color: 'var(--effect-modal-overlay)',
    },
  }),
  Pagination: Mantine.Pagination.extend({
    defaultProps: {
      radius: 'var(--radius-sm)',
      hideWithOnePage: true,
      previousIcon: ChevronLeftIconSm,
      nextIcon: ChevronRightIconSm,
      dotsIcon: MoreIconSm,
      gap: '0px',
      size: 'sm',
    },
    classNames: {
      root: styles.pagination,
      control: styles.paginationControl,
      dots: styles.paginationDots,
    },
  }),
  PasswordInput: Mantine.PasswordInput.extend({
    defaultProps: {
      size: 'md',
      visibilityToggleIcon: ({ reveal }) => (reveal ? <EyeIconSm /> : <EyeOffIconSm />),
    },
  }),
  PillsInput: Mantine.PillsInput.extend({
    defaultProps: {
      size: 'md',
    },
    classNames: {
      input: styles.pillsInput,
      wrapper: styles.pillsInputWrapper,
    },
  }),
  Popover: Mantine.Popover.extend({
    defaultProps: {
      withArrow: false,
      width: '300px',
      radius: 'var(--mantine-radius-xs)',
    },
    classNames: {
      dropdown: styles.popoverDropdown,
    },
  }),
  Progress: Mantine.Progress.extend({
    defaultProps: {
      radius: 'var(--mantine-radius-md)',
      size: 'md',
      striped: false,
      animated: false,
    },
    classNames: {
      root: styles.progress,
      section: styles.progressSection,
    },
  }),
  Radio: Mantine.Radio.extend({
    defaultProps: {
      color: 'var(--content-primary)',
      size: 'md',
      variant: 'outline',
    },
    classNames: {
      root: styles.radio,
      radio: styles.radioInput,
      label: styles.inputControlLabel,
      labelWrapper: styles.radioLabelWrapper,
      icon: styles.radioIcon,
    },
  }),
  RadioGroup: Mantine.RadioGroup.extend({
    classNames: {
      error: styles.radioGroupError,
    },
  }),
  ScrollArea: Mantine.ScrollArea.extend({
    classNames: {
      content: styles.scrollAreaContent,
      scrollbar: styles.scrollAreaScrollbar,
      viewport: styles.scrollAreaViewport,
    },
  }),
  SegmentedControl: Mantine.SegmentedControl.extend({
    defaultProps: {
      transitionDuration: 200,
      transitionTimingFunction: 'linear',
      withItemsBorders: false,
    },
    classNames: {
      root: styles.segmentedControl,
      label: styles.segmentedControlLabel,
      indicator: styles.segmentedControlIndicator,
      innerLabel: styles.segmentedControlInnerLabel,
      input: styles.segementedControlInput,
    },
  }),
  Select: Mantine.Select.extend({
    defaultProps: {
      allowDeselect: false,
      rightSection: <ChevronDownIconSm />,
      withCheckIcon: false,
    },
    classNames: {
      wrapper: styles.selectWrapper,
      root: styles.select,
      dropdown: styles.selectDropdown,
      option: styles.selectOption,
      section: styles.selectSection,
      input: styles.selectInput,
    },
  }),
  Slider: Mantine.Slider.extend({
    classNames: {
      track: styles.sliderDefaultTrack,
    },
    styles: {
      thumb: {
        borderWidth: 1,
        backgroundColor: 'var(--surface-primary)',
        borderColor: 'var(--surface-inverse-primary)',
      },
    },
  }),
  RangeSlider: Mantine.RangeSlider.extend({
    classNames: {
      bar: styles.sliderBar,
      label: styles.sliderLabel,
      mark: styles.sliderMark,
      markWrapper: styles.sliderMarkWrapper,
      root: styles.slider,
      thumb: styles.sliderThumb,
      track: styles.sliderTrack,
      trackContainer: styles.sliderTrackContainer,
    },
  }),
  Stepper: Mantine.Stepper.extend({
    classNames: {
      step: styles.stepperStep,
      stepBody: styles.stepperStepBody,
      separator: styles.stepperSeparator,
    },
  }),
  // https://mantine.dev/core/switch/
  Switch: Mantine.Switch.extend({
    defaultProps: {
      onLabel: <CheckIconXs />,
      offLabel: <CloseIconXs />,
      size: 'sm',
      withThumbIndicator: false,
    },
    classNames: {
      root: styles.switch,
      body: styles.switchBody,
      track: styles.switchTrack,
      trackLabel: styles.switchTrackLabel,
      input: styles.switchInput,
      thumb: styles.switchThumb,
    },
  }),
  Table: Mantine.Table.extend({
    classNames: {
      table: styles.table,
      td: styles.tableTd,
      th: styles.tableTh,
      tr: styles.tableTr,
      thead: styles.tableHead,
    },
  }),
  Tabs: Mantine.Tabs.extend({
    defaultProps: {
      color: 'var(--border-primary)',
      radius: 0,
    },
    classNames: {
      list: styles.tabList,
      tab: styles.tabsTab,
    },
  }),
  // https://mantine.dev/core/text/
  Text: Mantine.Text.extend({
    classNames: {
      root: styles.text,
    },
  }),
  Textarea: Mantine.Textarea.extend({
    classNames: {
      input: styles.textarea,
    },
  }),
  TextInput: Mantine.TextInput.extend({
    defaultProps: {
      size: 'md',
    },
  }),
  ThemeIcon: Mantine.ThemeIcon.extend({
    classNames: {
      root: styles.themeIcon,
    },
  }),
  TimePicker: TimePicker.extend({
    defaultProps: {
      withDropdown: true,
      format: TIME_PICKER_FORMAT,
      popoverProps: {
        width: 'fit-content',
      },
    },
    classNames: {
      control: styles.timePickerControl,
      controlsList: styles.timePickerControlList,
      controlsListGroup: styles.timePickerControlListGroup,
      dropdown: styles.timePickerDropdown,
      fieldsGroup: styles.timePickerFieldsGroup,
    },
  }),
  // https://mantine.dev/core/title/
  Title: Mantine.Title.extend({
    classNames: {
      root: styles.heading,
    },
  }),
  Tooltip: Mantine.Tooltip.extend({
    defaultProps: {
      position: 'right',
      withArrow: true,
      arrowSize: 8,
      offset: { mainAxis: 12 },
      radius: 'var(--mantine-radius-xs2)',
      multiline: true,
    },
    classNames: {
      tooltip: styles.tooltip,
    },
  }),
  UnstyledButton: Mantine.UnstyledButton.extend({
    classNames: {
      root: styles.unstyledButton,
    },
  }),
};
